import * as React from 'react';
import { useQuery } from '@apollo/client';
import { TablePaginationConfig, SorterResult } from 'antd/lib/table/interface';

import { setData, UA_MY_TASK_PAGE_SIZE } from 'models/LocalStorage';
import Config from 'models/Config';

import {
  GET_USER_TASKS,
  GetUserTasksData as Data,
  GetUserTasksVars as Vars
} from '../queries';
import { setupPusher, teardownPusher } from '../pusher';
import {
  UserTask,
  UserTaskOrder,
  USER_TASKS_PAGE_SIZE,
  UserTaskFilter
} from '../types';
import {
  orderTaskByColumn,
  getPreferenceOrderBy,
  setPreferenceOrderBy
} from '../sort';

import useFilter from '../hooks/useFilter';
import {
  GET_DASHBOARD_TASK,
  DashboardTaskData,
  DashboardTaskVars
} from './TaskDetailsDrawer/queries';
import { PreviewedTaskInfo } from './TaskDetailsDrawer';
import {
  dashboardFiltersAsLegacyFilter,
  dashboardSortAsTaskOrder,
  mergeLegacyFilterIntoDashboardFilters,
  useDashboardTaskSearch
} from './dashboardTaskSearchState';

export enum KindFilterKey {
  MyTasks = 'my_tasks',
  AllTasks = 'all_tasks'
}

const useUserTasksPageSize = (): [number, (size: number) => void] => {
  const [userTasksPageSize, _setUserTasksPageSize] = React.useState<number>(
    USER_TASKS_PAGE_SIZE
  );

  const setUserTasksPageSize = (size: number) => {
    _setUserTasksPageSize(size);
    setData(UA_MY_TASK_PAGE_SIZE, size);
  };

  return [userTasksPageSize, setUserTasksPageSize];
};

export const useState = ({
  organizationId,
  currentUserId,
  config,
  dashboardSearchEnabled
}: {
  organizationId: string;
  currentUserId: string;
  config: Config;
  dashboardSearchEnabled: boolean;
}) => {
  const [legacyPageNumber, setLegacyPageNumber] = React.useState<number>(1);
  const [legacyTaskOrderBy, setLegacyTaskOrderBy] = React.useState<
    UserTaskOrder
  >(getPreferenceOrderBy());

  const [pusherUpdated, setPusherUpdated] = React.useState<boolean>(false);
  const [previewedTaskInfo, setPreviewedTaskInfo] = React.useState<
    PreviewedTaskInfo
  >({ id: undefined, apiKey: undefined });

  const [userTasks, setUserTasks] = React.useState<UserTask[]>([]);
  const [userTasksPageSize, setUserTasksPageSize] = useUserTasksPageSize();

  const [legacyFilter, setLegacyUserTaskFilter] = useFilter({
    setPageNumber: setLegacyPageNumber
  });
  const [legacyIsAllTasks, setLegacyIsAllTasks] = React.useState<boolean>(
    false
  );
  const [
    legacyDescriptionSearchValue,
    setLegacyDescriptionSearchValue
  ] = React.useState<string>('');

  const dashboardSearch = useDashboardTaskSearch({
    organizationId,
    currentUserId,
    config,
    enabled: dashboardSearchEnabled,
    perPage: userTasksPageSize
  });

  const {
    data: legacyData,
    loading: legacyLoading,
    error: legacyError,
    refetch: legacyRefetch
  } = useQuery<Data, Vars>(GET_USER_TASKS, {
    skip: dashboardSearchEnabled,
    variables: {
      organizationId,
      offset: (legacyPageNumber - 1) * userTasksPageSize,
      limit: userTasksPageSize,
      orderBy: legacyTaskOrderBy,
      filter: {
        ...legacyFilter,
        kind: legacyIsAllTasks ? KindFilterKey.AllTasks : KindFilterKey.MyTasks,
        description: legacyDescriptionSearchValue
      }
    }
  });

  const isAllTasks = dashboardSearchEnabled
    ? dashboardSearch.state.scope === KindFilterKey.AllTasks
    : legacyIsAllTasks;
  const setIsAllTasks = (allTasks: boolean) => {
    if (dashboardSearchEnabled) {
      dashboardSearch.setScope(
        allTasks ? KindFilterKey.AllTasks : KindFilterKey.MyTasks
      );
    } else {
      setLegacyIsAllTasks(allTasks);
    }
  };
  const descriptionSearchValue = dashboardSearchEnabled
    ? dashboardSearch.draftQuery
    : legacyDescriptionSearchValue;
  const setDescriptionSearchValue = dashboardSearchEnabled
    ? dashboardSearch.setQuery
    : setLegacyDescriptionSearchValue;
  const submitDescriptionSearch = dashboardSearchEnabled
    ? dashboardSearch.submitQuery
    : setLegacyDescriptionSearchValue;
  const pageNumber = dashboardSearchEnabled
    ? dashboardSearch.state.page
    : legacyPageNumber;
  const taskOrderBy = dashboardSearchEnabled
    ? dashboardSortAsTaskOrder(
        dashboardSearch.state.sortField,
        dashboardSearch.state.sortDirection
      )
    : legacyTaskOrderBy;
  const setTaskOrderBy = (orderBy: UserTaskOrder) => {
    setPreferenceOrderBy(orderBy);
    if (dashboardSearchEnabled) {
      dashboardSearch.setSort(orderBy);
    } else {
      setLegacyTaskOrderBy(orderBy);
    }
  };
  const filter = dashboardSearchEnabled
    ? dashboardFiltersAsLegacyFilter(dashboardSearch.state.filters)
    : legacyFilter;
  const setUserTaskFilter = (nextFilter?: UserTaskFilter) => {
    if (dashboardSearchEnabled) {
      dashboardSearch.setFilters(
        mergeLegacyFilterIntoDashboardFilters(
          dashboardSearch.state.filters,
          nextFilter
        )
      );
    } else {
      setLegacyUserTaskFilter(nextFilter);
    }
  };
  const data = dashboardSearchEnabled ? dashboardSearch.data : legacyData;
  const loading = dashboardSearchEnabled
    ? dashboardSearch.loading
    : legacyLoading;
  const error = dashboardSearchEnabled ? dashboardSearch.error : legacyError;
  const refetch = dashboardSearchEnabled
    ? dashboardSearch.refetch
    : legacyRefetch;

  const {
    data: taskData,
    previousData: previousTaskData,
    loading: taskLoading,
    error: taskError,
    refetch: refetchTask
  } = useQuery<DashboardTaskData, DashboardTaskVars>(GET_DASHBOARD_TASK, {
    variables: {
      id: previewedTaskInfo.id ? String(previewedTaskInfo.id) : ''
    },
    skip: !previewedTaskInfo.id,
    fetchPolicy: 'cache-and-network'
  });
  const currentTask = taskData?.currentUser.task || null;
  const staleTask = previousTaskData?.currentUser.task || null;
  /* Keep the drawer mounted (rather than unmounting/remounting) while a new
   * task's data is in flight by falling back to the previously loaded task. */
  const previewedTask = currentTask || staleTask || null;
  const isSwitchingTask =
    !!previewedTaskInfo.id &&
    String(previewedTask?.id) !== String(previewedTaskInfo.id);

  React.useEffect(() => {
    if (!data) return;

    const projects = data.currentUser.userTasks.map(({ project }) => project);

    setupPusher({
      config: data.config,
      projects,
      onUpdate: () => setPusherUpdated(true)
    });

    return () =>
      teardownPusher({
        config: data.config,
        projects
      });
  }, [data]);

  React.useEffect(() => {
    if (loading) return;

    if (data) {
      setUserTasks(data.currentUser.userTasks);
      setPusherUpdated(false);
    }
  }, [data, loading]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Partial<Record<keyof UserTask, string[]>>,
    sorter: SorterResult<UserTask> | SorterResult<UserTask>[]
  ) => {
    if (!sorter) return;

    const orderBy: UserTaskOrder = orderTaskByColumn(
      sorter as SorterResult<UserTask>
    );
    setTaskOrderBy(orderBy);
  };

  const handlePagination = (page: number) => {
    if (dashboardSearchEnabled) {
      dashboardSearch.setPage(page);
    } else {
      setLegacyPageNumber(page);
    }
  };

  return {
    isAllTasks,
    setIsAllTasks,
    descriptionSearchValue,
    setDescriptionSearchValue,
    submitDescriptionSearch,
    previewedTaskInfo,
    setPreviewedTaskInfo,
    pageNumber,
    pusherUpdated,
    userTasks,
    userTasksPageSize,
    setUserTasksPageSize,
    dashboardSearchState: dashboardSearch.state,
    dashboardSearchPagination: dashboardSearch.pagination,
    setDashboardSearchFilters: dashboardSearch.setFilters,
    clearDashboardSearch: dashboardSearch.clearSearch,
    taskOrderBy,
    handleTableChange,
    handlePagination,
    userTaskFilter: filter,
    setUserTaskFilter,
    getUserTask: {
      previewedTask,
      taskLoading,
      taskError,
      refetchTask,
      isSwitchingTask
    },
    getUserTasks: { data, loading, error, refetch }
  };
};
